import type { AccessCatalogItemPublic } from '@accounts/shared';
import { useQueries } from '@tanstack/react-query';
import { type ReactNode, useCallback, useState } from 'react';

import { STR } from '../../../i18n';
import { listGrants } from '../../../lib/api';
import { queryKeys } from '../../../lib/queryKeys';
import { EmptyScreen, Skeleton } from '../../../ui/Primitives';
import { primaryButtonStyle, quietButtonStyle } from '../../../ui/styles';

import { AddCatalogRowDialog } from './catalog/AddCatalogRowDialog';
import { CatalogRowDrawer } from './catalog/CatalogRowDrawer';
import { CatalogRowsTable } from './catalog/CatalogRowsTable';
import { LevelNodes, LevelsEditor } from './catalog/HierarchySections';
import { FoldCard } from './catalog/Layout';
import {
  useAccessCatalogAll,
  useAccessLevels,
  useAccessNodesAll,
  useAccessRecordsAll,
  useApplicationsList,
} from './catalog/queries';

/**
 * THE ACCESS CATALOG (FINAL demo R3-2, spec rows 1 and 2): one page of collapsible
 * sections — Catalog rows, Hierarchy, then one section per level — with Expand all /
 * Collapse all. A row opens in a side drawer (Row settings, People with access, Row
 * history). The route guard admits an administrator, and HR or IT only with the "See
 * Catalog" switch (SIR-7864). For them it is `readOnly`: the same page with every catalog
 * write gone or disabled. The server refuses those writes to them independently.
 *
 * NOT BUILT HERE: the demo's "Queue and deactivation sync" section. Its settings (the
 * overdue-after-N-days value, `deprovision_overdue_hours`, per-app sync status) have no
 * endpoint in PR #1 — overdue is fixed server-side for wave 1 — so there is nothing real to
 * edit yet (spec row 1, open item 1d).
 */

type FoldKey = 'rows' | 'hierarchy' | `level:${string}`;

export function CatalogPanel({ readOnly = false }: { readOnly?: boolean }) {
  const levels = useAccessLevels();
  const nodes = useAccessNodesAll();
  const catalog = useAccessCatalogAll();
  const records = useAccessRecordsAll(catalog.data ?? []);
  const applications = useApplicationsList();

  // Rows open by default (it is what people come here for); the settings sections start
  // closed, as in the demo. Plain state: every visit starts the same way.
  const [folds, setFolds] = useState<Partial<Record<FoldKey, boolean>>>({ rows: true });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Stable, so the drawer's and dialog's focus handling is not re-run by refetches here.
  const closeDrawer = useCallback(() => {
    setSelectedId(null);
  }, []);
  const closeAdd = useCallback(() => {
    setAdding(false);
  }, []);

  const items = catalog.data ?? [];
  const appIds = [
    ...new Set(
      items
        .map((item) => item.application?.id ?? null)
        .filter((id): id is string => id !== null),
    ),
  ];
  // App rows' holders are the app's live grants — read from Grants, never counted twice.
  const grantQueries = useQueries({
    queries: appIds.map((applicationId) => ({
      queryKey: queryKeys.grantsForApplication(applicationId, false),
      queryFn: () => listGrants({ applicationId, includeRevoked: false }),
    })),
  });

  if (
    levels.isError ||
    nodes.isError ||
    catalog.isError ||
    records.isError ||
    applications.isError
  ) {
    return (
      <EmptyScreen
        title={STR.access.catalog.loadFailedTitle}
        body={STR.access.catalog.loadFailedBody}
        action={
          <button
            type="button"
            className="prim"
            style={primaryButtonStyle}
            onClick={() => {
              void levels.refetch();
              void nodes.refetch();
              void catalog.refetch();
              records.refetch();
              void applications.refetch();
            }}
          >
            {STR.common.tryAgain}
          </button>
        }
      />
    );
  }

  if (
    levels.data === undefined ||
    nodes.data === undefined ||
    catalog.data === undefined ||
    records.data === undefined ||
    applications.data === undefined
  ) {
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[1, 2, 3].map((key) => (
          <Skeleton key={key} style={{ height: 52, width: '100%', borderRadius: 12 }} />
        ))}
      </div>
    );
  }

  const levelList = levels.data;
  const nodeList = nodes.data;
  const recordList = records.data;
  const applicationList = applications.data.data;

  const grantCount = new Map<string, number>();
  appIds.forEach((applicationId, index) => {
    const data = grantQueries[index]?.data;
    if (data !== undefined) grantCount.set(applicationId, data.data.length);
  });

  const holders = (item: AccessCatalogItemPublic): number | null => {
    if (item.application === null) {
      return recordList.filter((record) => record.catalogItem.id === item.id).length;
    }
    return grantCount.get(item.application.id) ?? null;
  };

  const live = items.filter((item) => item.retiredAt === null);
  const keys: FoldKey[] = [
    'rows',
    'hierarchy',
    ...levelList.map((level): FoldKey => `level:${level.id}`),
  ];
  const isOpen = (key: FoldKey): boolean => folds[key] === true;
  const anyOpen = keys.some(isOpen);
  const toggle = (key: FoldKey) => {
    setFolds((state) => {
      const next: Partial<Record<FoldKey, boolean>> = { ...state };
      next[key] = state[key] !== true;
      return next;
    });
  };
  const setAll = (open: boolean) => {
    const next: Partial<Record<FoldKey, boolean>> = {};
    for (const key of keys) next[key] = open;
    setFolds(next);
  };

  const selected = selectedId === null ? undefined : items.find((item) => item.id === selectedId);
  const levelNames = levelList.map((level) => level.name).join(STR.access.catalog.levelArrow);

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1240 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="tx-subtle" style={{ fontSize: 12.5 }}>
          {STR.access.catalog.intro(levelNames === '' ? STR.common.dash : levelNames)}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              setAll(true);
            }}
          >
            {STR.access.catalog.expandAll}
          </button>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            disabled={!anyOpen}
            onClick={() => {
              setAll(false);
            }}
          >
            {STR.access.catalog.collapseAll}
          </button>
        </span>
      </div>

      <FoldCard
        title={STR.access.catalog.rowsTitle}
        count={live.length}
        aside={STR.access.catalog.rowsAside(
          live.filter((item) => !item.requestable).length,
          live.filter((item) => item.hrCanGrant).length,
          live.filter((item) => item.application !== null).length,
        )}
        open={isOpen('rows')}
        onToggle={() => {
          toggle('rows');
        }}
      >
        <CatalogRowsTable
          items={items}
          holders={holders}
          onOpen={setSelectedId}
          onAdd={
            readOnly
              ? undefined
              : () => {
                  setAdding(true);
                }
          }
        />
      </FoldCard>

      <FoldCard
        title={STR.access.catalog.hierarchyTitle}
        count={levelList.length}
        aside={levelNames}
        open={isOpen('hierarchy')}
        onToggle={() => {
          toggle('hierarchy');
        }}
      >
        <ReadOnlyFields readOnly={readOnly}>
          <LevelsEditor levels={levelList} nodes={nodeList} items={items} />
        </ReadOnlyFields>
      </FoldCard>

      {levelList.map((level) => {
        const atLevel = nodeList.filter((node) => node.level.id === level.id);
        const key = `level:${level.id}` as const;
        return (
          <FoldCard
            key={level.id}
            title={level.name}
            count={atLevel.filter((node) => node.retiredAt === null).length}
            aside={STR.access.catalog.levelAside(
              level.depth,
              atLevel.filter((node) => node.retiredAt !== null).length,
            )}
            open={isOpen(key)}
            onToggle={() => {
              toggle(key);
            }}
          >
            <ReadOnlyFields readOnly={readOnly}>
              <LevelNodes level={level} nodes={nodeList} items={items} />
            </ReadOnlyFields>
          </FoldCard>
        );
      })}

      {selected === undefined ? null : (
        <CatalogRowDrawer
          key={selected.id}
          item={selected}
          nodes={nodeList}
          applications={applicationList}
          records={recordList}
          readOnly={readOnly}
          onClose={closeDrawer}
        />
      )}
      {adding && !readOnly ? (
        <AddCatalogRowDialog
          nodes={nodeList}
          applications={applicationList}
          onClose={closeAdd}
          onAdded={(item) => {
            setAdding(false);
            setSelectedId(item.id);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The hierarchy sections hold nothing but editors (rename, add, move, retire). For a
 * view-only reader a disabled fieldset keeps every name readable and every control inert.
 */
function ReadOnlyFields({ readOnly, children }: { readOnly: boolean; children: ReactNode }) {
  if (!readOnly) return <>{children}</>;
  return (
    <fieldset disabled style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
      {children}
    </fieldset>
  );
}
